/**
 * Auth service for managing user authentication and authorization
 */
import StorageService from './StorageService';
import User, { USER_ROLES } from '../models/User';

class AuthService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('users');
    this.currentUserKey = 'clinic_current_user';
    this.tokenKey = 'clinic_auth_token';
    this.initializeDefaultAdmin();
  }

  /**
   * Initialize default admin user and practitioners if no users exist
   * @private
   */
  initializeDefaultAdmin() {
    // Check if we have any users
    const users = this.storageService.getAll();
    if (Object.keys(users).length === 0) {
      // Create default admin
      const adminUser = new User({
        username: 'admin',
        password: this.hashPassword('admin123'), // In a real app, this would be more secure
        name: '演示账户(管理员)',
        role: USER_ROLES.ADMIN,
        isActive: true
      });

      this.storageService.save(adminUser.id, adminUser.toJSON());
      console.log('Default admin user created');
    }
  }


  /**
   * Simple password hashing (for demo purposes)
   * In a real app, use a proper hashing library
   * @param {string} password - Plain text password
   * @returns {string} Hashed password
   * @private
   */
  hashPassword(password) {
    // This is NOT secure - just for demo purposes
    // In production, use bcrypt or similar
    return `hashed_${password}_${password.split('').reverse().join('')}`;
  }

  /**
   * Verify password
   * @param {string} plainPassword - Plain text password
   * @param {string} hashedPassword - Hashed password
   * @returns {boolean} Whether password is correct
   * @private
   */
  verifyPassword(plainPassword, hashedPassword) {
    // Simple verification to match our simple hashing
    return this.hashPassword(plainPassword) === hashedPassword;
  }

  /**
   * Login user
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Object} Login result with user and token if successful
   */
  login(username, password) {
    // Find user with this username
    const users = this.storageService.find(user =>
      user.username === username && user.isActive !== false
    );

    if (users.length === 0) {
      return {
        success: false,
        message: '用户名或密码错误'
      };
    }

    const userData = users[0];

    // Check password
    if (!this.verifyPassword(password, userData.password)) {
      return {
        success: false,
        message: '用户名或密码错误'
      };
    }

    // Create User instance to ensure proper permissions are set
    const user = new User(userData);

    // Create session token (simple implementation)
    const token = this.generateToken(user.id);

    // Store current user and token (without password)
    const userWithoutPassword = { ...user.toJSON() };
    delete userWithoutPassword.password;

    localStorage.setItem(this.currentUserKey, JSON.stringify(userWithoutPassword));
    localStorage.setItem(this.tokenKey, token);

    return {
      success: true,
      user: userWithoutPassword,
      token
    };
  }

  /**
   * Generate a simple token
   * @param {string} userId - User ID
   * @returns {string} Token
   * @private
   */
  generateToken(userId) {
    // This is a very simple token generation method
    // In a real app, use JWT or similar
    const randomPart = Math.random().toString(36).substring(2);
    const timestamp = new Date().getTime();
    return `${userId}_${timestamp}_${randomPart}`;
  }

  /**
   * Log out current user
   */
  logout() {
    localStorage.removeItem(this.currentUserKey);
    localStorage.removeItem(this.tokenKey);
    return { success: true };
  }

  /**
   * Get current user
   * @returns {Object|null} Current user or null if not logged in
   */
  getCurrentUser() {
    const userJson = localStorage.getItem(this.currentUserKey);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch (e) {
      return null;
    }
  }

  /**
   * Get current token
   * @returns {string|null} Authentication token or null if not logged in
   */
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Whether user is authenticated
   */
  isAuthenticated() {
    return this.getToken() !== null && this.getCurrentUser() !== null;
  }

  /**
   * Check if user has specific role
   * @param {string} role - Role to check
   * @returns {boolean} Whether user has role
   */
  hasRole(role) {
    const user = this.getCurrentUser();
    if (!user) return false;
    return user.role === role;
  }

  /**
   * Check if user has admin privileges
   * @returns {boolean} Whether user is admin
   */
  isAdmin() {
    return this.hasRole(USER_ROLES.ADMIN);
  }

  /**
   * Create user
   * @param {Object} userData - User data
   * @returns {Object} Creation result
   */
  createUser(userData) {
    // Check if username is already taken
    const existingUsers = this.storageService.find(user =>
      user.username === userData.username
    );

    if (existingUsers.length > 0) {
      return {
        success: false,
        message: '用户名已被占用'
      };
    }

    // Hash password before storing
    const hashedPassword = userData.password ? this.hashPassword(userData.password) : '';

    // Create and save user
    const user = new User({
      ...userData,
      password: hashedPassword,
      isActive: userData.isActive !== false // Default to active
    });

    this.storageService.save(user.id, user.toJSON());

    // Return success without password
    const userWithoutPassword = { ...user };
    delete userWithoutPassword.password;

    return {
      success: true,
      user: userWithoutPassword
    };
  }

  /**
   * Update user
   * @param {string} id - User ID
   * @param {Object} userData - User data to update
   * @returns {Object} Update result
   */
  updateUser(id, userData) {
    // Get existing user
    const existingUser = this.storageService.getById(id);
    if (!existingUser) {
      return {
        success: false,
        message: '用户不存在'
      };
    }

    // If changing username, check if new username is available
    if (userData.username && userData.username !== existingUser.username) {
      const usersWithSameName = this.storageService.find(user =>
        user.username === userData.username && user.id !== id
      );

      if (usersWithSameName.length > 0) {
        return {
          success: false,
          message: '用户名已被占用'
        };
      }
    }

    // Handle password update if provided
    if (userData.password) {
      userData.password = this.hashPassword(userData.password);
    } else {
      // Don't update password if not provided
      delete userData.password;
    }

    // Update user
    const updatedUser = {
      ...existingUser,
      ...userData,
      updatedAt: new Date().toISOString()
    };

    this.storageService.save(id, updatedUser);

    // If updating current user, update stored user
    const currentUser = this.getCurrentUser();
    if (currentUser && currentUser.id === id) {
      const userWithoutPassword = { ...updatedUser };
      delete userWithoutPassword.password;
      localStorage.setItem(this.currentUserKey, JSON.stringify(userWithoutPassword));
    }

    // Return success without password
    const userWithoutPassword = { ...updatedUser };
    delete userWithoutPassword.password;

    return {
      success: true,
      user: userWithoutPassword
    };
  }

  /**
   * Delete user
   * @param {string} id - User ID
   * @returns {Object} Deletion result
   */
  deleteUser(id) {
    // Don't allow deleting current user
    const currentUser = this.getCurrentUser();
    if (currentUser && currentUser.id === id) {
      return {
        success: false,
        message: '不能删除当前登录用户'
      };
    }

    // Delete user
    const result = this.storageService.delete(id);

    return {
      success: result,
      message: result ? '用户已删除' : '删除失败'
    };
  }

  /**
   * Change user password
   * @param {string} id - User ID
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Object} Password change result
   */
  changePassword(id, currentPassword, newPassword) {
    // Get existing user
    const existingUser = this.storageService.getById(id);
    if (!existingUser) {
      return {
        success: false,
        message: '用户不存在'
      };
    }

    // Verify current password
    if (!this.verifyPassword(currentPassword, existingUser.password)) {
      return {
        success: false,
        message: '当前密码错误'
      };
    }

    // Update password
    existingUser.password = this.hashPassword(newPassword);
    existingUser.updatedAt = new Date().toISOString();

    this.storageService.save(id, existingUser);

    return {
      success: true,
      message: '密码已更新'
    };
  }

  /**
   * Reset user password (admin only)
   * @param {string} id - User ID
   * @param {string} newPassword - New password
   * @returns {Object} Password reset result
   */
  resetPassword(id, newPassword) {
    // Check if current user is admin
    if (!this.isAdmin()) {
      return {
        success: false,
        message: '需要管理员权限'
      };
    }

    // Get existing user
    const existingUser = this.storageService.getById(id);
    if (!existingUser) {
      return {
        success: false,
        message: '用户不存在'
      };
    }

    // Update password
    existingUser.password = this.hashPassword(newPassword);
    existingUser.updatedAt = new Date().toISOString();

    this.storageService.save(id, existingUser);

    return {
      success: true,
      message: '密码已重置'
    };
  }

  /**
   * Get all users (without passwords)
   * @returns {Array} All users
   */
  getAllUsers() {
    const users = this.storageService.getAllAsArray();

    // Remove passwords
    return users.map(user => {
      const userWithoutPassword = { ...user };
      delete userWithoutPassword.password;
      return userWithoutPassword;
    });
  }

  /**
   * Get user by ID (without password)
   * @param {string} id - User ID
   * @returns {Object|null} User or null if not found
   */
  getUserById(id) {
    const user = this.storageService.getById(id);
    if (!user) return null;

    // Remove password
    const userWithoutPassword = { ...user };
    delete userWithoutPassword.password;

    return userWithoutPassword;
  }

  /**
   * Search users by query (without passwords)
   * @param {string} query - Search query
   * @returns {Array} Matching users
   */
  searchUsers(query) {
    if (!query) return this.getAllUsers();

    const lowercaseQuery = query.toLowerCase();

    const users = this.storageService.find(user =>
      user.username.toLowerCase().includes(lowercaseQuery) ||
      user.name.toLowerCase().includes(lowercaseQuery) ||
      (user.email && user.email.toLowerCase().includes(lowercaseQuery))
    );

    // Remove passwords
    return users.map(user => {
      const userWithoutPassword = { ...user };
      delete userWithoutPassword.password;
      return userWithoutPassword;
    });
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;